import { pushNotification } from '@frontend/pole-ui/lib/components/Notification'
import type { AxiosError, AxiosInstance } from 'axios'
import axios from 'axios'
import { RESPONSE_ERRORS } from '@/api/client/responseErrorConstants'
import type { IAxiosFactoryParams, RequestConfig, ResponseConfig, TInternalAxiosRequestConfig } from '@/api/client/types'
import { getJwt } from '@/logic/auth/reducer'
import type { TAppStore } from '@/redux/interfaces'
import { AuthCookiesService } from '@/services'
import { ExecutionEnvironment } from '@/utils/ExecutionEnvironment'

let store: TAppStore

/**
 * @see https://redux.js.org/faq/code-structure#how-can-i-use-the-redux-store-in-non-component-files
 */
export const injectStoreToAxiosInstance = (_store: unknown) => {
  store = _store as TAppStore
}

export const createAxiosInstance = (params: IAxiosFactoryParams): AxiosInstance => {
  const { baseURL } = params
  //eslint-disable-next-line @typescript-eslint/no-explicit-any, unused-imports/no-unused-vars
  const { isSsr } = ExecutionEnvironment

  const instance = axios.create({
    baseURL,
    timeout: Number(process.env.API_RESPONSE_TIMEOUT) || 60_000,
  })

  instance.interceptors.request.use((config: TInternalAxiosRequestConfig) => {
    const token = AuthCookiesService.getJwt() || (store && getJwt()(store.getState?.()))

    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`
    }

    config.headers['X-Timezone'] = Intl.DateTimeFormat().resolvedOptions().timeZone

    if (config.meta?.headers?.['X-Request-Id']) {
      config.headers['X-Request-Id'] = config.meta.headers['X-Request-Id']
    }

    return config
  })

  instance.interceptors.response.use(undefined, data => {
    // Статуса может не быть в случае отмены запроса через AbortController
    const status = data.response?.status
    if (status === 500) pushNotification({ title: RESPONSE_ERRORS['500'], status: 'error' })
    if (status === 400) pushNotification({ title: RESPONSE_ERRORS['400'], status: 'warning' })

    return Promise.reject(data)
  })

  return instance
}

export const createAxiosClient = (params: IAxiosFactoryParams) => {
  const { baseURL } = params
  const instance = createAxiosInstance({ baseURL })

  const client = async <TData, TError = unknown, TVariables = unknown>(
    config: RequestConfig<TVariables>,
  ): Promise<ResponseConfig<TData>> => {
    const promise = instance.request<TVariables, ResponseConfig<TData>>({ ...config }).catch((e: AxiosError<TError>) => {
      throw e
    })

    return promise
  }

  return client
}
